import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, CustomInput, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import api from '~/services/api';

const TwoFactorRolesConfig = () => {
  const roleLabels = {
    administrator: 'Administrador',
    'store-manager': 'Gerente',
    'digital-consultant': 'Vendedor Digital',
    'cross-manager': 'Vendedor Cross',
    support: 'Suporte',
    master: 'Master',
  };

  const signedUser = useSelector(({ user }) => user.profile);

  const isAdmin =
    signedUser && signedUser.roles.find(role => role.slug === 'administrator');
  const isSupport =
    signedUser && signedUser.roles.find(role => role.slug === 'support');

  const baseURL = isAdmin || isSupport ? 'administrator' : 'master';

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null); // id da role sendo atualizada

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`${baseURL}/two-factor-roles`);
      setRoles(data.two_factor_role_settings);
    } catch (err) {
      toast.error('Erro ao carregar configurações de 2FA por cargo.');
    } finally {
      setLoading(false);
    }
  }, [baseURL]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleToggle = async role => {
    try {
      setUpdating(role.id);

      const { data } = await api.put(`${baseURL}/two-factor-roles/${role.id}`, {
        enabled: !role.enabled,
      });

      setRoles(currentRoles =>
        currentRoles.map(r =>
          r.id === role.id
            ? { ...r, enabled: data.two_factor_role_setting.enabled }
            : r
        )
      );

      toast.success(
        `2FA ${
          data.two_factor_role_setting.enabled ? 'ativado' : 'desativado'
        } para ${roleLabels[role.role_slug] || role.role_name}.`
      );
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Sem permissão para alterar esta configuração.');
      } else {
        toast.error('Erro ao atualizar configuração.');
      }
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <Row className="w-100 mx-0 mt-3 mb-2">
        <Col xs={12} className="px-0 d-flex align-items-center">
          <Spinner size="sm" color="secondary" className="mr-2" />
          <span className="text-muted font-size-13">
            Carregando configurações por cargo...
          </span>
        </Col>
      </Row>
    );
  }

  if (!roles.length) {
    return null;
  }

  return (
    <div className="mt-3 mb-2 pl-3" style={{ borderLeft: '3px solid #e2e8f0' }}>
      <Row className="w-100 mx-0 mb-2">
        <Col xs={12} className="px-0">
          <span className="font-size-13 font-weight-600 text-gray-700">
            Cargos que exigem 2FA
          </span>
        </Col>
      </Row>

      {roles.map(role => (
        <Row
          key={`2fa-role-${role.id}`}
          className="w-100 mx-0 mb-2 align-items-center"
        >
          <Col xs={8} lg={4} className="px-0">
            <span className="font-size-14 text-gray-700">
              {roleLabels[role.role_slug] || role.role_name}
            </span>
          </Col>
          <Col xs={4} lg={2} className="px-0">
            {updating === role.id ? (
              <Spinner size="sm" color="secondary" />
            ) : (
              <CustomInput
                type="switch"
                id={`2fa-role-switch-${role.id}`}
                checked={role.enabled}
                onChange={() => handleToggle(role)}
              />
            )}
          </Col>
        </Row>
      ))}
    </div>
  );
};

export default TwoFactorRolesConfig;
